import { useEffect } from 'react'
import { LEVEL_LABEL, LEVEL_TONE } from './announcementLevels'
import { useActiveAnnouncements } from './useActiveAnnouncements'

/** «1 объявление», «2 объявления», «5 объявлений». */
function plural(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'объявление'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'объявления'
  return 'объявлений'
}

/**
 * Объявление, которое нужно прочитать: окно при входе с кнопкой «Понятно».
 *
 * Несколько объявлений показываются по одному — закрыл первое, появилось
 * следующее. Клик по фону окно не закрывает: смысл такого объявления в том,
 * что человек его увидел. Крестик и Esc — то же, что «Понятно».
 */
export function AnnouncementModal() {
  const { modals, dismiss, dismissing } = useActiveAnnouncements()
  const current = modals[0]

  useEffect(() => {
    if (!current) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss(current.id)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [current, dismiss])

  if (!current) return null
  const left = modals.length - 1

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcement-title"
        className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-md"
      >
        <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs text-gray-500">{LEVEL_LABEL[current.level]}</div>
            <h3 id="announcement-title" className="text-sm font-semibold text-gray-800 break-words">
              {current.title}
            </h3>
          </div>
          <button
            type="button"
            aria-label="Закрыть"
            onClick={() => dismiss(current.id)}
            disabled={dismissing}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none disabled:opacity-40"
          >
            ×
          </button>
        </div>
        <div className="p-4">
          <div
            className={`max-h-[60vh] overflow-y-auto rounded-lg border px-3 py-3 text-sm whitespace-pre-line break-words ${LEVEL_TONE[current.level]}`}
          >
            {current.body}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          {left > 0 && (
            <span className="mr-auto text-xs text-gray-400">
              Ещё {left} {plural(left)}
            </span>
          )}
          <button
            type="button"
            autoFocus
            onClick={() => dismiss(current.id)}
            disabled={dismissing}
            className="px-4 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
          >
            Понятно
          </button>
        </div>
      </div>
    </div>
  )
}
